import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { assetsApi, type SearchResult } from "../api/assets";
import { ConditionChip } from "./ConditionChip";
import { pathLabel } from "../lib/paths";
import Ic from "./icons.jsx";

const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;

type Hit = { key: string; href: string; title: string; snippet: string; chip?: React.ReactNode };
type Group = { id: "assets" | "positions" | "types"; label: string; hits: Hit[] };

/**
 * Topbar global search (`GET /search?q=`, contract §5) after the portal's
 * `GlobalSearch` pattern: debounced input, results in three groups, arrow keys
 * + Enter, Escape / outside click closes, choosing a hit navigates and clears.
 */
export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const [result, setResult] = useState<SearchResult | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const q = term.trim();
    if (q.length < MIN_CHARS) { setResult(null); setLoading(false); return; }
    const ctrl = new AbortController();
    const h = setTimeout(() => {
      setLoading(true);
      assetsApi.search(q, ctrl.signal)
        .then((res) => { setResult(res); setActive(0); })
        .catch(() => { if (!ctrl.signal.aborted) setResult({ assets: [], positions: [], types: [] }); })
        .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    }, DEBOUNCE_MS);
    return () => { clearTimeout(h); ctrl.abort(); };
  }, [term]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const groups = useMemo<Group[]>(() => {
    if (!result) return [];
    const out: Group[] = [];
    if (result.assets.length) {
      out.push({
        id: "assets",
        label: t("search.group.assets", "Asseti"),
        hits: result.assets.map((a) => ({
          key: `a-${a.id}`,
          href: `/asseti/${a.id}`,
          title: `${a.tag} · ${a.inventoryNo}`,
          // Contract §3: type · manufacturer; where it is comes last.
          snippet: [
            a.typeName,
            a.manufacturer,
            a.positionId ? pathLabel(a.positionPath, a.positionName) : a.effectiveLocation?.name,
          ].filter(Boolean).join(" · "),
          chip: <ConditionChip condition={a.condition} />,
        })),
      });
    }
    if (result.positions.length) {
      out.push({
        id: "positions",
        label: t("search.group.positions", "Pozicije"),
        hits: result.positions.map((p) => ({
          key: `p-${p.id}`,
          href: `/struktura/${p.id}`,
          title: p.sequenceNo !== null ? `${p.name} #${p.sequenceNo}` : p.name,
          snippet: [p.code, pathLabel(p.path)].filter(Boolean).join(" · "),
        })),
      });
    }
    if (result.types.length) {
      out.push({
        id: "types",
        label: t("search.group.types", "Tipovi delova"),
        hits: result.types.map((x) => ({
          key: `t-${x.id}`,
          href: `/tipovi/${x.id}`,
          title: `${x.code} — ${x.name}`,
          snippet: x.description ?? "",
        })),
      });
    }
    return out;
  }, [result, t]);

  const flat = useMemo(() => groups.flatMap((g) => g.hits), [groups]);

  function choose(hit: Hit) {
    setOpen(false);
    setTerm("");
    setResult(null);
    inputRef.current?.blur();
    navigate(hit.href);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setOpen(true);
        setActive((a) => Math.min(flat.length - 1, a + 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActive((a) => Math.max(0, a - 1));
        break;
      case "Enter": {
        const hit = flat[active] ?? flat[0];
        if (hit) { e.preventDefault(); choose(hit); }
        break;
      }
      case "Escape":
        setOpen(false);
        inputRef.current?.blur();
        break;
    }
  }

  const q = term.trim();
  const showPanel = open && q.length >= MIN_CHARS;

  return (
    <div ref={wrapRef} className="search gs">
      <Ic.search aria-hidden="true" />
      <input
        ref={inputRef}
        type="search"
        role="combobox"
        aria-expanded={showPanel}
        aria-controls="gs-panel"
        aria-autocomplete="list"
        aria-label={t("search.aria", "Globalna pretraga")}
        placeholder={t("search.placeholder", "Pretraga: oznaka, pozicija, tip…")}
        value={term}
        onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {showPanel && (
        <div id="gs-panel" role="listbox" className="gs-panel dd-scroll">
          {loading && flat.length === 0 ? (
            <div className="gs-empty">{t("common.loading", "Učitavanje…")}</div>
          ) : flat.length === 0 ? (
            <div className="gs-empty">{t("search.empty", "Nema rezultata za „{{term}}“.", { term: q })}</div>
          ) : (
            groups.map((g) => (
              <div key={g.id} className="gs-group">
                <div className="gs-group-head">{g.label}</div>
                {g.hits.map((h) => {
                  const idx = flat.indexOf(h);
                  return (
                    <button
                      key={h.key}
                      type="button"
                      role="option"
                      aria-selected={idx === active}
                      className={`gs-hit${idx === active ? " is-active" : ""}`}
                      onMouseEnter={() => setActive(idx)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => choose(h)}
                    >
                      <span className="gs-hit-title">{h.title}</span>
                      {h.chip}
                      {h.snippet && <span className="gs-hit-snippet">{h.snippet}</span>}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
